<template>
  <div class="asc-doc-mgmt-container">
    <asc-page-loading-indicator :loading="isLoading">
      <asc-doc-mgmt-template
        :tabs="tabs"
        :user="user"
        :help-about-grid-data="getHelpAboutGridData"
        :help-about-grid-disabled="userGuidesLoading"
        :help-about-contents="helpAboutContents"
        :upload-new-doc-disabled="
          isLoading && getAvailableCompanies && getAvailableCompanies.length
        "
        :deselect-all-user-guides="deselectAllUserGuides"
        v-on="$listeners"
        @onGridCellClick="handleUserGuideGridCellClick"
        @onUploadNewDoc="handleUploadNewUserGuideDoc"
        @onContentSave="handleContentSave"
        @onContentChangeCancel="handleHelpAboutContenCancel"
        @onHelpAboutGridReady="handleHelpAboutGridReady"
      ></asc-doc-mgmt-template>
    </asc-page-loading-indicator>

    <asc-doc-mgmt-dialog
      :is-confirmation="isConfirmation"
      :show-dialog="showConfirmationDialog"
      :on-save-exit-session="handleUserConfirmation"
      :on-continue-session="handleContinueSession"
    />

    <asc-doc-mgmt-help-about-dialog
      :show-dialog="helpAboutDialogShow"
      :files="files"
      :companies="
        isHelpAboutDocEdit ? getUpdateCompanyList : getAvailableCompanies
      "
      :selected-companies="selectedCompanies"
      :on-close="handleUserGuideDialogClose"
      :on-accept="handleUserGuideSave"
      :is-edit="isHelpAboutDocEdit"
      :user-guide-id="currentUserGuideId"
      @removeFile="handleUserGuideDocRemove"
    />

    <asc-pdf-viewer-dialog
      title="Preview"
      :loading="pdfLoading"
      :show-dialog="pdfViewerDialogShow"
      :pdf-document-u-r-l="pdfDocumentURL"
      :on-close="handlePdfViewerDialogClose"
    />
  </div>
</template>

<script>
import AscPageLoadingIndicator from '@/components/molecules/AscPageLoadingIndicator'
import AscDocMgmtTemplate from '@/components/templates/AscDocMgmtTemplate'
import AscDocMgmtDialog from '@/components/molecules/AscDocMgmtDialog'
import AscPdfViewerDialog from '@/components/molecules/AscPdfViewerDialog'
import AscDocMgmtHelpAboutDialog from '@/components/molecules/AscDocMgmtHelpAboutDialog'

import { mapState, mapGetters, mapActions } from 'vuex'
import { USER_CONSTS as UC, USER_GUIDE_CONSTS as UGC } from '@/stores/consts'

export default {
  name: 'AscDocMgmtContainer',

  components: {
    AscPageLoadingIndicator,
    AscDocMgmtTemplate,
    AscDocMgmtDialog,
    AscDocMgmtHelpAboutDialog,
    AscPdfViewerDialog,
  },

  props: {
    onFooterAction: {
      type: Object,
      default: () => {},
    },
    user: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      isConfirmation: false,
      showConfirmationDialog: false,
      currentUserGuideId: null,
      tabs: [
        {
          id: 1,
          title: 'Help & About',
          headerText: 'Help Documents',
          active: true, // Remove this once all the tabs are completed
        },
        {
          id: 2,
          title: 'Privacy Statement',
        },
        {
          id: 3,
          title: 'Release Notes',
        },
        {
          id: 4,
          title: 'Terms & Conditions',
        },
      ],
      helpAboutDialogShow: false,
      isHelpAboutDocEdit: false,
      files: [],
      selectedCompanies: [],
      pdfViewerDialogShow: false,
      pdfLoading: false,
      pdfDocumentURL: '',
      deselectAllUserGuides: false,
      userGuideGridAPI: null,
    }
  },

  computed: {
    ...mapGetters(UGC.NAMESPACE, ['helpAboutContents']),
    ...mapState(UC.NAMESPACE, ['companyList']),
    ...mapState(UGC.NAMESPACE, [
      'userGuides',
      'userContents',
      'userGuidesLoading',
      'userContentsLoading',
    ]),

    isLoading() {
      return this.userGuidesLoading || this.userContentsLoading
    },

    getHelpAboutGridData() {
      return this.companyList && this.companyList.length
        ? [
            ...this.userGuides.map(userGuide => {
              const companyNames = userGuide.companyIds.map(companyId => {
                const company = this.companyList.find(
                  company => company.id === companyId,
                )
                return company.name
              })

              return {
                ...userGuide,
                ...{ companyIds: userGuide.companyIds, company: companyNames },
              }
            }),
          ]
        : []
    },

    getAlreadySelectedCompanyIds() {
      return this.userGuides.map(({ companyIds }) => companyIds).flat()
    },

    getUpdateCompanyList() {
      const selectedCompanyIds = this.userGuides.find(
        userGuide => userGuide.id === this.currentUserGuideId,
      ).companyIds

      return this.companyList.filter(
        company =>
          !this.getAlreadySelectedCompanyIds.includes(company.id) ||
          selectedCompanyIds.includes(company.id),
      )
    },

    getAvailableCompanies() {
      return this.companyList.filter(
        company => !this.getAlreadySelectedCompanyIds.includes(company.id),
      )
    },
  },

  watch: {
    onFooterAction(newValue) {
      if (newValue) this.handleFooterAction(newValue)
    },
  },

  created() {
    this.getCompanies()
    this.getUserGuides()
    this.getAllContents()
  },

  mounted() {
    this.getHelpAboutGridData()
    this.getHelpAboutContents()
  },

  methods: {
    ...mapActions(UC.NAMESPACE, [UC.GET_COMPANY_LIST]),

    ...mapActions(UGC.NAMESPACE, [
      UGC.GET_ALL_USER_GUIDES,
      UGC.SAVE_USER_GUIDE,
      UGC.UPDATE_USER_GUIDE,
      UGC.UPDATE_USER_GUIDE_BY_ID,
      UGC.GET_ALL_USER_CONTENTS,
      UGC.SAVE_USER_CONTENT,
      UGC.GET_USER_GUIDE_BY_ID,
      UGC.DELETE_USER_GUIDE,
    ]),

    async getCompanies() {
      await this[UC.GET_COMPANY_LIST]()
    },

    handleUserConfirmation() {},

    handleContinueSession() {},

    // Help & About
    async getUserGuides() {
      await this[UGC.GET_ALL_USER_GUIDES]()
    },

    async getAllContents() {
      await this[UGC.GET_ALL_USER_CONTENTS]()
    },

    async handleUserGuideGridCellClick(action, data) {
      if (action === 'preview' && data.id) {
        this.pdfLoading = true
        this.pdfViewerDialogShow = true
        // Preview Document in PDF viewer
        const userGuideURL = await this[UGC.GET_USER_GUIDE_BY_ID](data.id)
        this.pdfDocumentURL = userGuideURL
        this.pdfLoading = false
      } else if (action === 'editDocument') {
        this.files = [
          {
            lastModified: data.lastUpdated,
            displayName: data.filename,
            fileSizeInBytes: data.fileSizeInBytes,
            type: 'application/pdf',
          },
        ]
        this.currentUserGuideId = data.id
        this.selectedCompanies = data.companyIds
        this.isHelpAboutDocEdit = true
        this.helpAboutDialogShow = true
      }
    },

    handleHelpAboutGridReady(api) {
      if (api) this.userGuideGridAPI = api
    },

    handlePdfViewerDialogClose() {
      this.pdfViewerDialogShow = false
      this.pdfDocumentURL = ''
    },

    handleUploadNewUserGuideDoc() {
      // Reset files and selected companies to empty
      this.files = []
      this.selectedCompanies = []
      this.helpAboutDialogShow = true
    },

    handleUserGuideSave(userGuide) {
      if (userGuide.new) {
        this[UGC.SAVE_USER_GUIDE](userGuide)
      } else if (userGuide.update) {
        this[UGC.UPDATE_USER_GUIDE](userGuide)
      }
    },

    handleUserGuideDialogClose() {
      this.isHelpAboutDocEdit = false
      this.helpAboutDialogShow = false
      this.currentUserGuideId = null
      this.selectedCompanies = []
    },

    async handleUserGuideDocRemove(file) {
      if (file) {
        this.files = []
        if (Number.isInteger(file.id)) {
          this[UGC.UPDATE_USER_GUIDE_BY_ID]({
            remove: true,
            id: file.id,
          })
        }
      }
    },

    async handleContentSave(content) {
      if (content && content.typeId) await this[UGC.SAVE_USER_CONTENT](content)
    },

    handleHelpAboutContenCancel() {
      // Content change Canceled
    },

    async handleFooterAction({ action, selectedRows }) {
      if (action === 'cancel') {
        if (this.userGuideGridAPI) this.userGuideGridAPI.deselectAll()
      } else if (action === 'delete' && selectedRows.length) {
        this.$emit('onFooterActionDisabled', true)
        await this[UGC.DELETE_USER_GUIDE](selectedRows)
        if (this.userGuideGridAPI) this.userGuideGridAPI.deselectAll()
        this.$emit('onFooterActionDisabled', false)
        this.$emit('OnFooterActionComplete', action, selectedRows)
      }
    },
  },
}
</script>
